/**
 * ═══ LA LANGUE QUE LE VISITEUR PRÉFÈRE ═══
 *
 * Trois sources, dans l'ordre de la V1 (`hooks/useLanguages.jsx`) : ce que son COMPTE
 * porte, ce que ce navigateur a retenu, puis la langue du navigateur lui-même. À défaut,
 * c'est la langue de référence de l'événement qui parle, et c'est le châssis qui la donne.
 *
 * ⚠️ LE COMPTE PASSE DEVANT, ET C'EST TOUT L'INTÉRÊT : changer de langue ici l'écrit sur
 * l'utilisateur, donc il la retrouve depuis un autre appareil, dans un e-mail, l'année
 * suivante. Le stockage local ne sert qu'à ceux qui n'ont pas encore de session, et à
 * éviter que la page s'affiche une fraction de seconde en français avant que le compte
 * arrive.
 *
 * Ce module est PUR pour être testable : il ne connaît ni React ni le réseau, et la
 * langue du navigateur lui est DONNÉE plutôt que lue, sans quoi ses cas ne tiendraient
 * que sur la machine qui les exécute.
 */

const KEY = "gk.languageId";

/** Ce que ce navigateur a retenu, s'il a retenu quelque chose de lisible. */
export function readLanguage(storage: Pick<Storage, "getItem"> | undefined): number | undefined {
  try {
    const raw = storage?.getItem(KEY);
    const id = Number(raw);
    return raw !== null && Number.isInteger(id) && id > 0 ? id : undefined;
  } catch {
    /* Un navigateur peut refuser son propre stockage (mode privé, réglage strict) :
       lever ici emporterait le démarrage du site pour une préférence d'affichage. */
    return undefined;
  }
}

export function writeLanguage(
  storage: Pick<Storage, "setItem"> | undefined,
  id: number,
): void {
  try {
    storage?.setItem(KEY, String(id));
  } catch {
    /* Même raison : ne pas retenir est un désagrément, planter est une panne. */
  }
}

/**
 * LA LANGUE DU NAVIGATEUR, si l'événement la propose.
 *
 * ⚠️ SUR LES DEUX PREMIÈRES LETTRES, en majuscules : la base nomme ses langues « FR »,
 * « EN », quand le navigateur dit « fr-FR » ou « en-US ». C'est le calcul de la V1
 * (`navigatorLanguage.substring(0, 2).toUpperCase()`), repris tel quel.
 */
export function browserLanguage(
  tag: string | undefined,
  languages: ReadonlyArray<{ id: number; short: string }>,
): number | undefined {
  const code = (tag ?? "").slice(0, 2).toUpperCase();
  if (!code) return undefined;
  return languages.find((l) => l.short.toUpperCase() === code)?.id;
}

/**
 * CELLE QU'IL FAUT SERVIR, ou `undefined` pour laisser parler la langue de référence.
 *
 * ⚠️ FILTRÉE CONTRE CE QUE L'ÉVÉNEMENT PROPOSE. Un identifiant gardé d'un autre événement
 * — le stockage local est partagé par tout le domaine — désignerait une langue que
 * celui-ci ne sert pas : la cascade ne trouverait rien et la page s'afficherait vide de
 * ses textes.
 */
export function preferredLanguage({
  account,
  stored,
  browser,
  languages,
}: {
  /** `Users.languageId`, quand la personne est connectée. */
  account?: number | null;
  /** Ce que ce navigateur a retenu. */
  stored?: number | undefined;
  /** L'étiquette du navigateur, « fr-FR ». */
  browser?: string | undefined;
  languages: ReadonlyArray<{ id: number; short: string }>;
}): number | undefined {
  const offered = (id: number | null | undefined) =>
    typeof id === "number" && languages.some((l) => l.id === id) ? id : undefined;

  return (
    offered(account) ??
    offered(stored) ??
    offered(browserLanguage(browser, languages))
  );
}

/**
 * LA LANGUE À POSER SUR UN COMPTE QUI N'EN A PAS, ou `undefined` s'il n'y a rien à écrire
 * (bug #331).
 *
 * Avant la session, le choix du sélecteur ne va que dans ce navigateur : sans ce
 * rattrapage, un compte ouvert ensuite (créé par code, connecté par lien ou par mot de
 * passe) restait sans langue, et ses emails partaient dans la langue de référence.
 *
 * ⚠️ SEULEMENT UN CHOIX. Le stockage n'est écrit que par le geste du visiteur
 * (`chooseLanguage`) ; la langue du navigateur, que la personne n'a pas choisie, n'est
 * jamais écrite sur son compte.
 *
 * ⚠️ ET JAMAIS PAR-DESSUS CELLE DU COMPTE : elle a pu être choisie sur un autre appareil,
 * c'est elle qui fait foi.
 */
export function languageToAdopt(
  account: number | null | undefined,
  stored: number | undefined,
): number | undefined {
  return account ? undefined : stored;
}
